// @flow
import * as React from 'react';
import { memoize } from 'lodash';
import moment from 'moment';
import {
    TextInput, MaskedInput, ResponsiveContext, Stack,
} from 'grommet/es6';
import { defineMessages } from 'react-intl';
import * as Yup from 'yup';
import FieldWrapper from './FieldWrapper';
import StyledCloseButton from './StyledCloseButton';


type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: string;
        name: string;
    },
    form: {
        setFieldValue: (string, any) => void,
    },
    required: boolean,
    label: string,
    help_text: string,
    format?: string,
    mapFormatRegex?: string,
}

const mask = [{
    length: 2,
    regexp: /^([01]?[0-9]|2[0-3])$/,
    placeholder: 'HH',
}, {
    fixed: ':',
}, {
    length: 2,
    regexp: /^[0-5][0-9]$|^[0-9]$/,
    placeholder: 'MM',
}];


class TimeInput extends React.Component<Props> {
    static defaultProps = {
        format: 'HH:mm',
        mapFormatRegex: /^([01]\d|2[0-3]):?([0-5]\d)$/,
    };

    constructor(props: Props) {
        super(props);

        this.getMask = memoize(this.getMask);
    }

    getMask = (format: string) => {
        const date = moment().format(format);

        let { mapFormatRegex } = this.props;
        mapFormatRegex = mapFormatRegex || TimeInput.defaultProps.mapFormatRegex;

        const values = (date.match(new RegExp(mapFormatRegex, 'g')) || []).map((val) => ({
            regexp: mapFormatRegex,
            placeholder: val,
            length: [1, val.length],
        }));
        const delimiters = date.replace(new RegExp(mapFormatRegex, 'g'), '').split('').map(
            (d) => ({ fixed: d }),
        );

        return values.map((v, i) => (
            [v, delimiters[i]]
        )).reduce((a, b) => a.concat(b)).filter((m) => !!m);
    };

    handleMaskedChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
        const { form: { setFieldValue }, field: { name } } = this.props;
        setFieldValue(name, e.target.value);
    };

    onClear = () => {
        const { form: { setFieldValue }, field: { name } } = this.props;
        setFieldValue(name, null);
    };

    render() {
        const {
            field: {
                name, onBlur, onChange, value,
            },
            label,
            help_text: help,
            required,
            format,
        } = this.props;

        return (
            <FieldWrapper
                name={name}
                required={required}
                helpText={help}
                label={label}
                placeholder=""
            >
                <ResponsiveContext.Consumer>
                    {
                        (size) => {
                            switch (size) {
                            case 'small':
                                return (
                                    <Stack anchor="right">
                                        <TextInput
                                            type="time"
                                            name={name}
                                            onChange={onChange}
                                            placeholder=""
                                            onBlur={onBlur}
                                            value={value || ''}
                                            id={`id-${name}`}
                                        />
                                        {value && <StyledCloseButton onClick={this.onClear} plain />}
                                    </Stack>
                                );
                            default:
                                return (
                                    <Stack anchor="right">
                                        <MaskedInput
                                            mask={mask}
                                            name={name}
                                            onChange={this.handleMaskedChange}
                                            onBlur={onBlur}
                                            value={value || ''}
                                            id={`id-${name}`}
                                            placeholder=""
                                        />
                                        {value && <StyledCloseButton onClick={this.onClear} plain />}
                                    </Stack>
                                );
                            }
                        }
                    }
                </ResponsiveContext.Consumer>
            </FieldWrapper>
        );
    }
}

const messages = defineMessages({
    required: {
        id: 'time-required',
        defaultMessage: 'This field is required.',
    },
});

TimeInput.getValidationSchema = (field: { validation: Object }) => {
    if (field.validation.required) {
        return Yup.string().required(messages.required).nullable();
    }

    return Yup.string().nullable();
};

export default TimeInput;
